import React, { useCallback, useRef, useState } from 'react';
import { Modal, Platform, Pressable, StyleSheet, View, useWindowDimensions } from 'react-native';
import { installMenuGate } from './messageMenuGate';

/** The message's «…» button on screen when its menu opens (window coordinates). */
export type MenuAnchor = { x: number; y: number; width: number; height: number };

/**
 * CORE-311 — the message menu on mobile web floats over the thread instead of
 * opening inside it: inline, it pushed every message below it down.
 *
 * It opens from the «…» button, downwards; upwards only when there is no room
 * below (the last messages). Horizontally it grows away from the bubble — to
 * the left of an own message's «…», to the right of an incoming one's — and is
 * kept on screen. A tap anywhere outside the card closes it.
 *
 * The card's height decides the direction, so the first frame is measured
 * invisibly. Without `overlay` the card renders in place, as before (desktop).
 *
 * CORE-1730 (возврат QA 31.10) — меню открывается и долгим нажатием пальца по
 * сообщению, и тогда слой появляется под ещё прижатым пальцем. По отпусканию
 * браузер синтезирует `click` уже по подложке (а когда карточка раскрыта вверх —
 * по её пункту), и меню закрывалось через мгновение, не дав выбрать действие.
 * Слой принимает click только после жеста, начатого на нём самом (указатель,
 * касание, клавиша); хвост открывающего нажатия гасится в фазе захвата.
 * Взводит `pointerdown`, а `mousedown` — только вне хвоста касания: после
 * касания Chrome синтезирует совместимые mousedown/mouseup/click уже по
 * подложке, а pointerdown — нет.
 * Слушатели — на DOM-узле: capture-пропы click и mouse RNW в View не передаёт.
 *
 * CORE-2058 — пункты срабатывают и у скринридеров (виртуальный click, пара
 * mousedown + click); сама логика и её тест — в `messageMenuGate`.
 */
export function MessageMenuLayer({ overlay, anchor, alignEnd, onClose, children }: {
  overlay: boolean;
  anchor: MenuAnchor | null;
  /** Own message: the card's right edge lines up with the «…». */
  alignEnd: boolean;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const { width: winW, height: winH } = useWindowDimensions();
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  const unmountGate = useRef<(() => void) | null>(null);
  const gateRef = useCallback((node: unknown) => {
    unmountGate.current?.();
    unmountGate.current = null;
    if (Platform.OS !== 'web' || !isDomNode(node)) return;
    // Жест, начатый на слое, «взводит» его; click до этого — хвост открывающего нажатия
    // (CORE-2058: кроме активаций скринридера — см. messageMenuGate).
    unmountGate.current = installMenuGate(node, node.ownerDocument ?? node);
  }, []);
  if (!overlay) return <>{children}</>;
  if (!anchor) return null;

  let place = { left: 0, top: 0 };
  if (size) {
    const below = anchor.y + anchor.height + GAP;
    const fitsBelow = below + size.h <= winH - EDGE;
    const top = fitsBelow ? below : anchor.y - GAP - size.h;
    const left = alignEnd ? anchor.x + anchor.width - size.w : anchor.x;
    place = {
      top: clamp(top, EDGE, winH - EDGE - size.h),
      left: clamp(left, EDGE, winW - EDGE - size.w),
    };
  }

  return (
    // CORE-1791 (возврат QA 04.11) — у диалога есть имя (axe: aria-dialog-name),
    // а подложка — кнопка «Закрыть меню»: подпись без роли на вебе — div с
    // запрещённым aria-label. Пропсы Modal react-native-web отдаёт узлу диалога.
    <Modal transparent visible animationType="none" onRequestClose={onClose} {...({ 'aria-label': MENU_NAME } as object)}>
      <View ref={gateRef as React.Ref<View>} style={StyleSheet.absoluteFill} testID="chat-menu-gate">
        <Pressable style={StyleSheet.absoluteFill} onPress={onClose} accessibilityRole="button" accessibilityLabel="Закрыть меню" testID="chat-menu-backdrop" />
        <View
          style={[styles.slot, place, !size && styles.measuring]}
          onLayout={(e) => {
            const { width: w, height: h } = e.nativeEvent.layout;
            if (!size || size.w !== w || size.h !== h) setSize({ w, h });
          }}
          testID="chat-menu-layer"
        >
          {children}
        </View>
      </View>
    </Modal>
  );
}

function isDomNode(node: unknown): node is HTMLElement {
  return !!node && typeof (node as HTMLElement).addEventListener === 'function';
}

/** Имя диалога меню — то же, что у кнопки «…», которая его открывает. */
const MENU_NAME = 'Действия с сообщением';
/** Space between the «…» and the card. */
const GAP = 4;
/** Keeps the card off the very edge of the screen. */
const EDGE = 8;

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(value, max));
}

const styles = StyleSheet.create({
  slot: { position: 'absolute' },
  measuring: { opacity: 0 },
});
